import { useState } from "react";

interface SubmitModalProps {
  promptId: number;
  onClose: () => void;
  onSuccess: () => void;
}

export function SubmitModal({ promptId, onClose, onSuccess }: SubmitModalProps) {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const wordCount = body.trim().split(/\s+/).filter((w) => w.length > 0).length;

  const insertFormatting = (format: "bold" | "italic") => {
    const textarea = document.getElementById("story-body") as HTMLTextAreaElement;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = body.substring(start, end);
    const wrapper = format === "bold" ? "**" : "*";
    const newText =
      body.substring(0, start) +
      wrapper +
      (selectedText || "text") +
      wrapper +
      body.substring(end);
    setBody(newText);
    setTimeout(() => {
      textarea.focus();
      const newCursorPos = start + wrapper.length + (selectedText || "text").length + wrapper.length;
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !body.trim()) {
      setError("Title and story are required");
      return;
    }
    if (wordCount > 3000) {
      setError("Story exceeds 3,000 word limit");
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const res = await fetch("/api/submit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ promptId, title: title.trim(), body: body.trim() }),
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || "Failed to submit");
      }

      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to submit");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2 className="modal-title">Submit Your Story</h2>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="story-title">Title</label>
            <input
              id="story-title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Enter your story title"
              maxLength={200}
            />
          </div>

          <div className="form-group">
            <label htmlFor="story-body">Story (Markdown)</label>
            <div className="toolbar">
              <button
                type="button"
                className="toolbar-btn"
                onClick={() => insertFormatting("bold")}
                title="Bold"
              >
                <strong>B</strong>
              </button>
              <button
                type="button"
                className="toolbar-btn"
                onClick={() => insertFormatting("italic")}
                title="Italic"
              >
                <em>I</em>
              </button>
            </div>
            <textarea
              id="story-body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Write your story here... Use **bold** and *italic* for formatting."
              rows={15}
            />
            <div className="word-count">
              {wordCount} / 3,000 words
              {wordCount > 3000 && <span className="word-count-error"> (over limit)</span>}
            </div>
          </div>

          {error && <div className="error-message">{error}</div>}

          <div className="modal-actions">
            <button type="button" className="btn-secondary" onClick={onClose} disabled={submitting}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={submitting || wordCount > 3000}>
              {submitting ? "Submitting..." : "Submit"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
